/**
 * YoutubeInlinePlayer — WEB.
 *
 * POR QUE NÃO USAR `react-native-youtube-iframe` AQUI:
 * na web ele cai em `react-native-web-webview`, que renderiza um `<iframe>` com
 * **`srcDoc`** (o HTML do player embutido). Um iframe com `srcDoc` tem **origem
 * opaca (null)**, e a API do YouTube depende de `postMessage` com origem válida
 * — o handshake falha e o vídeo simplesmente não toca. (O pacote também é de
 * 2018 e usa `componentWillReceiveProps`, já removido do React.)
 *
 * Aqui usamos a API oficial do YouTube na PRÓPRIA página: o iframe é criado por
 * `YT.Player` com origem real, então controle e eventos funcionam de verdade.
 *
 * Contrato: só as props que os chamadores usam hoje — `videoId`, `height`,
 * `width`, `play`, `onReady`, `onChangeState`, `initialPlayerParams`. As props
 * de WebView (`webViewStyle`, `webViewProps`) são aceitas e ignoradas, para o
 * call-site continuar idêntico nas duas plataformas.
 */
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { View } from 'react-native';

const API_SRC = 'https://www.youtube.com/iframe_api';
let apiPromise = null;

/** Carrega a IFrame API uma única vez por página. */
const loadYouTubeApi = () => {
    if (typeof window === 'undefined') return Promise.reject(new Error('sem window'));
    if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
    if (apiPromise) return apiPromise;

    apiPromise = new Promise((resolve, reject) => {
        // A API chama este global ao terminar de carregar. Encadeamos para não
        // sobrescrever um handler que outra parte da página tenha registrado.
        const anterior = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
            try { anterior?.(); } catch { /* handler de terceiro nunca derruba o nosso */ }
            resolve(window.YT);
        };
        const script = document.createElement('script');
        script.src = API_SRC;
        script.async = true;
        script.onerror = () => reject(new Error('falha ao carregar a API do YouTube'));
        document.head.appendChild(script);
    });
    return apiPromise;
};

// YT.PlayerState -> as mesmas strings que o `react-native-youtube-iframe` emite,
// para o `onChangeState` do chamador não precisar saber em que plataforma está.
const ESTADOS = {
    '-1': 'unstarted',
    0: 'ended',
    1: 'playing',
    2: 'paused',
    3: 'buffering',
    5: 'video cued',
};

// `forwardRef` só para aceitar o `ref` que os chamadores passam sem gerar aviso
// do React. Nenhum deles chama método no ref hoje; se um dia precisar, expor a
// API imperativa aqui com `useImperativeHandle`.
const YoutubeInlinePlayer = forwardRef(({
    videoId,
    height,
    width,
    play = false,
    onReady,
    onChangeState,
    initialPlayerParams = {},
}, _ref) => {
    const hostRef = useRef(null);
    const playerRef = useRef(null);
    // Guardam os callbacks mais recentes sem recriar o player a cada render.
    const onReadyRef = useRef(onReady);
    const onChangeStateRef = useRef(onChangeState);
    onReadyRef.current = onReady;
    onChangeStateRef.current = onChangeState;

    const [apiPronta, setApiPronta] = useState(false);
    // `onReady` só pode ser avisado UMA vez (evento do YouTube ou teto de tempo).
    const prontoAvisadoRef = useRef(false);
    const avisarPronto = () => {
        if (prontoAvisadoRef.current) return;
        prontoAvisadoRef.current = true;
        onReadyRef.current?.();
    };

    useEffect(() => {
        let vivo = true;
        loadYouTubeApi().then(() => { if (vivo) setApiPronta(true); }).catch(() => {});
        return () => { vivo = false; };
    }, []);

    // Avisa "pronto" JÁ NA MONTAGEM, antes de existir player.
    //
    // Os chamadores usam `videoId={isPlayerReady ? videoId : ''}` — ou seja, só
    // entregam o vídeo depois do onReady. Se esperássemos o onReady do YouTube,
    // o player nasceria SEM vídeo e teríamos que carregá-lo depois, quando os
    // métodos do iframe ainda podem não estar expostos — foi exatamente assim
    // que a 1ª versão travou (nenhum vídeo do YouTube tocava).
    // Invertendo a ordem, o `videoId` chega ANTES de criarmos o player e ele já
    // nasce com o vídeo certo. Sem corrida, sem estado pendente.
    useEffect(() => {
        avisarPronto();
    }, []);

    useEffect(() => {
        // Espera o `videoId` de verdade: criar o player vazio é o que gerava a
        // corrida descrita acima.
        if (!apiPronta || !videoId || !hostRef.current || playerRef.current) return undefined;

        // ⚠️ `new YT.Player(el)` SUBSTITUI `el` por um <iframe>. Nunca entregar a
        // ele um nó que o React gerencia: na primeira reconciliação o React
        // tentaria mexer num elemento que não existe mais (e o player sumiria a
        // cada re-render — o overlay do feed re-renderiza o tempo todo, porque
        // anima posição). Criamos um filho descartável só para o YouTube consumir;
        // o React continua dono apenas do container.
        const alvo = document.createElement('div');
        alvo.style.width = '100%';
        alvo.style.height = '100%';
        hostRef.current.appendChild(alvo);

        playerRef.current = new window.YT.Player(alvo, {
            width: '100%',
            height: '100%',
            videoId,
            playerVars: {
                // O chamador manda `preventFullScreen`/`rel` no vocabulário do
                // pacote nativo; traduzimos para os parâmetros do player.
                autoplay: play ? 1 : 0,
                controls: initialPlayerParams.controls === false ? 0 : 1,
                modestbranding: initialPlayerParams.modestbranding ? 1 : 0,
                rel: initialPlayerParams.rel === false ? 0 : 1,
                fs: initialPlayerParams.preventFullScreen ? 0 : 1,
                playsinline: 1,
                // A API recomenda declarar a origem; sem isso alguns navegadores
                // recusam o postMessage de volta para a página.
                origin: window.location.origin,
            },
            events: {
                onReady: () => avisarPronto(),
                onStateChange: (e) => onChangeStateRef.current?.(ESTADOS[String(e?.data)] ?? 'unstarted'),
            },
        });


        return () => {
            try { playerRef.current?.destroy?.(); } catch { /* destruir nunca pode lançar */ }
            playerRef.current = null;
            try { alvo.remove(); } catch { /* já removido pelo destroy */ }
        };
        // Só (api, primeiro videoId) criam o player. Trocar de vídeo depois é
        // tratado no efeito abaixo, sem recriar o iframe.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiPronta, !!videoId]);

    // Troca de vídeo com o player JÁ existente (o primeiro vem no construtor).
    useEffect(() => {
        const p = playerRef.current;
        if (!p || !videoId || typeof p.loadVideoById !== 'function') return;
        try {
            if (play) p.loadVideoById(videoId);
            else p.cueVideoById(videoId);
        } catch { /* troca de vídeo nunca pode derrubar a tela */ }
    }, [videoId, play]);

    return (
        <View style={{ width: width ?? '100%', height, backgroundColor: '#000000', overflow: 'hidden' }}>
            <div ref={hostRef} style={{ width: '100%', height: '100%' }} />
        </View>
    );
});

YoutubeInlinePlayer.displayName = 'YoutubeInlinePlayer';

export default YoutubeInlinePlayer;
